import {
  FC,
  PropsWithChildren,
  createContext,
  useContext,
  useMemo,
  useCallback,
  useEffect
} from 'react'
import VersionBanner from './VersionBanner/VersionBanner'
import { AVAILABLE_ENVS } from './types'
import { useSelector } from 'react-redux'
import {
  getSavedAppBuildDate,
  getSavedAppVersion
} from '../../redux/store/common/getters'
import { getHideAppVersionParam } from '../../helper/common'
import { notification } from '../../components/Notification'
import { BTN_TXT, NOTIFICATIONS } from '../../constants'
import { Button, BUTTON_TYPES } from '../../components/Button'
import { onRefreshPage, showVersionNotification } from './helpers'

type TDevAppVersion = {
  appVersion?: string
  env?: keyof typeof AVAILABLE_ENVS
}

export const DevAppVersionContext = createContext<TDevAppVersion>({
  appVersion: undefined,
  env: undefined
})

const DevAppVersionProvider: FC<PropsWithChildren> = ({ children }) => {
  const appVersion = useSelector(getSavedAppVersion)
  const appBuildDate = useSelector(getSavedAppBuildDate)

  const _showUpdateVersionNotification = useCallback(
    () =>
      notification.info({
        message: NOTIFICATIONS.UPDATE_IS_REQUIRED,
        btn: (
          <Button
            type={BUTTON_TYPES.PRIMARY}
            onClick={onRefreshPage}
            upperCase
            inline
            small
          >
            {BTN_TXT.REFRESH_NOW}
          </Button>
        )
      }),
    []
  )

  useEffect(() => {
    if (appBuildDate) {
      showVersionNotification(appBuildDate, _showUpdateVersionNotification)
    }
  }, [_showUpdateVersionNotification, appBuildDate])

  const env = useMemo(
    (): keyof typeof AVAILABLE_ENVS | undefined =>
      process.env.REACT_APP_ACTIVE_ENV as keyof typeof AVAILABLE_ENVS,
    []
  )

  const _envForBanner = useMemo(
    () => (!getHideAppVersionParam() ? env : undefined),
    [env]
  )

  const context = useMemo(
    () => ({
      appVersion,
      env
    }),
    [appVersion, env]
  )

  return (
    <DevAppVersionContext.Provider value={context}>
      <VersionBanner version={appVersion} env={_envForBanner} />
      {children}
    </DevAppVersionContext.Provider>
  )
}

export const useAppVersion = () => useContext(DevAppVersionContext)

export default DevAppVersionProvider
